<template>
  <div>
    <div v-if="$slots['header']">
      <slot name="header"></slot>
    </div>

    <div ref="virtualScrollArea" class="virtual-scroll-area" :style="{ height: scrollAreaPixelHeight + 'px' }" @scroll="onScroll">
      <div class="scroll-container" :style="{ height: totalHeight + 'px' }">
        <div class="items-wrapper" :style="{ transform: `translateY(${topPaddingHeight}px)`, transition: itemsWrapperTransition }">
          <div
            v-for="item in renderItems"
            :key="item[itemIdAttribute]"
            :id="item[itemIdAttribute]"
            :style="{ height: item[itemPixelHeightAttribute] ? item[itemPixelHeightAttribute] + 'px' : defaultItemPixelHeight + 'px' }"
          >
            <slot name="item" v-bind:item="item"></slot>
          </div>
        </div>
      </div>
    </div>
  </div>
</template>

<script>
/**
 * This component provides a very fast and flexibel way to scroll a huge list of items by rendering only the visible parts.
 * Rows may differ in height, however they need to define a height or the default height will be used.
 * Contrary to some other virtual scrollers it is possible to scroll programmatically to an item with a given id. To use this feature,
 * assign the virtual-scroll-list a ref, e.g. <virtual-scroll-list ref="vscroller" ...></virtual-scroll-list> and scroll to an id like so: $refs['vscroller'].scrollToRowWithItemId('ID_400').
 * Each item needs to contain an id attribute, which contains it's id.
 * 
 * This component defines two slots. One for a header (optional) and one for the item. Use like so:
 *  <virtual-scroll-list
                ref="vscroller"
                :items="items"
                :scrollAreaPixelHeight="200"
                itemIdAttribute="id"
                itemPixelHeightAttribute="height"
              >
                <template v-slot:header>Header</template>
                <template v-slot:item="{ item }">
                  <div>Row at index {{ item.index }} with label {{ item.label }}</div>
                </template>
              </virtual-scroll-list>
 * 
 */
export default {
  name: 'VirtualScrollList',
  props: {
    items: Array, // sorted list of items to render

    scrollAreaPixelHeight: { type: Number, required: true }, // The height of the scroll area in pixels
    defaultItemPixelHeight: { type: Number, default: 50 }, // The default row height used when items define no item height

    itemPixelHeightAttribute: { type: String, default: 'itemHeight' }, // Attribute name of the item defining the item's height
    itemIdAttribute: { type: String, default: 'id' }, // Attribute name of the item defining the item's id
  },
  created() {
    this.init();
  },
  data() {
    return {
      rowFrom: 0,
      rowTo: 0,

      totalHeight: null,
      itemTopArray: [],
      itemIndexByItemId: {},

      topPaddingHeight: 0,

      itemsWrapperTransition: '',
    };
  },
  computed: {
    renderItems() {
      return this.items ? this.items.slice(this.rowFrom, this.rowTo + 1) : [];
    },
  },
  methods: {
    /**
     * Externally usable method to make the scroller scroll to the item with the id <itemId>
     */
    scrollToRowWithItemId(itemId) {
      console.debug('scrollToRowWithItemId');
      let index = this.itemIndexByItemId[itemId];
      if (index !== undefined) {
        this.itemsWrapperTransition = 'transform 2.2s';

        let itemTop = this.itemTopArray[index];
        this.scrollToViewportPosition(itemTop);

        setTimeout(() => {
          this.$refs.virtualScrollArea.scrollTop = itemTop;
          this.itemsWrapperTransition = '';
        }, 100);
      }
    },
    init() {
      let itemIndexByItemId = {};
      let itemTopArray = [];
      let itemTop = 0;
      if (!this.items) return;

      this.items.forEach((item, index) => {
        itemTopArray.push(itemTop);
        let itemId = item[this.itemIdAttribute] ? item[this.itemIdAttribute] : `ID_${index}`;
        itemIndexByItemId[itemId] = index;
        itemTop += item[this.itemPixelHeightAttribute] ? item[this.itemPixelHeightAttribute] : this.defaultItemPixelHeight;
      });
      this.totalHeight = itemTop >= this.scrollAreaPixelHeight ? itemTop : this.scrollAreaPixelHeight;
      console.debug('calculated totalHeight', this.totalHeight);
      this.itemTopArray = itemTopArray;
      this.itemIndexByItemId = itemIndexByItemId;
      this.scrollToViewportPosition(0);
      if (this.$refs.virtualScrollArea) {
        this.$refs.virtualScrollArea.scrollTop = 0;
      }
    },
    onScroll() {
      if (this.$refs.virtualScrollArea) {
        let yTop = this.$refs.virtualScrollArea.scrollTop;
        this.scrollToViewportPosition(yTop);
      }
    },
    scrollToViewportPosition(yTop) {
      // Remark: If everything is getting rendered, than the following code is used to scroll into the view:
      // el.scrollIntoView({ behavior: 'smooth', block: 'center', inline: 'center' }); // scrolls element into center
      // The nature of a virtual scroller is though to not render all. Thus scrolling means just rendering the appropriate part in this context.
      let yBottom = yTop + this.scrollAreaPixelHeight;
      // find items to render
      let firstIndex = this.itemTopArray.findIndex((i) => i >= yTop);
      let lastIndex = this.itemTopArray.findIndex((i) => i > yBottom);
      if (lastIndex === -1) lastIndex = this.items.length - 1;
      this.rowFrom = Math.max(0, firstIndex - 5);
      this.rowTo = Math.min(lastIndex + 5, this.items.length - 1);
      // console.debug('this.itemTopArray[this.rowTo] - this.itemTopArray[this.rowFrom]', this.itemTopArray[this.rowTo] - this.itemTopArray[this.rowFrom]);
      if (this.itemTopArray[this.rowTo] - this.itemTopArray[this.rowFrom] < this.scrollAreaPixelHeight) {
        // console.debug("It's likely we are at the end of the list and do not fill the vertical space. So add some more rows");
        this.rowFrom = Math.max(0, this.rowFrom - 20);
      }
      // console.debug('render rows', this.rowFrom, 'to', this.rowTo);
      this.topPaddingHeight = this.itemTopArray[this.rowFrom];
    },
  },
  watch: {
    items(newValue, oldValue) {
      if (newValue.length !== oldValue.length) {
        console.debug('virtual scroll list items length changed');
        this.init();
      }
    },
  },
};
</script>

<style scoped>
.virtual-scroll-area {
  overflow-y: scroll;
}
</style>
